import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import Skeleton from "react-loading-skeleton";
import Layout from '../../layouts/PayrollLayout';
import { getToken } from "../../utils/tokenUtils";
import { useEmploymentDetails } from "../../contexts/EmploymentManagementContext";
import { AuthContext } from "../../contexts/AuthContext";
import Modal from "../../components/UI/Modal";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import EmploymentDetailForm from "../../components/Form/EmploymentDetailForm";
import Drawer from "../../components/UI/Drawer";
import ReactPaginate from 'react-paginate';
import { X, Briefcase, MoreVertical, Edit, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import StatusBadge from '../../components/UI/StatusBadge';

export default function EmploymentDetailsManagement() {
  const { employmentDetails, fetchEmploymentDetails, addEmploymentDetails, updateEmploymentDetails, deleteEmploymentDetails, pagination, loading } = useEmploymentDetails(); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const token = getToken();
  const { user } = useContext(AuthContext);
  const [selectedEmployment, setSelectedEmployment] = useState(null);
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  
  const sections = [
    { id: 'basic', label: 'Basic Info', icon: <Briefcase size={18} /> },
    { id: 'organization', label: 'Organization', icon: <Building size={18} /> },
    { id: 'dates', label: 'Important Dates', icon: <Calendar size={18} /> },
    { id: 'location', label: 'Location', icon: <MapPin size={18} /> },
    { id: 'grade', label: 'Grade & Salary', icon: <Award size={18} /> },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.dropdown-container')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Function to determine dropdown position
  const handleDropdownToggle = (userId, event) => {
    if (openDropdownId === userId) {
      setOpenDropdownId(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 150; // Approximate dropdown height
      
      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      
      setOpenDropdownId(userId);
    }
  };

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRequest(null);
  };

  const openDrawerForDetails = (employment) => {
    setSelectedEmployment(employment);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedEmployment(null);
  };

  const handleEmploymentDetailSubmit = useCallback(
    async (employeeDetail) => {
      try {
        if (editingRequest) {
          const employeeDetailData = employeeDetail instanceof FormData ? convertFormDataToObject(employeeDetail) : employeeDetail;
          await updateEmploymentDetails({ ...editingRequest, ...employeeDetailData });
          // setMessage("Employee Details updated successfully.");
          // setNotification("Employee Details successfully.");
          setNotification({
            message: "Employee Details updated successfully.",
            type: "success"
          });
        } else {

          await addEmploymentDetails(employeeDetail);
          // setMessage("Employee Detail added successfully.");
          // setNotification("Employee Detail added successfully.");
          setNotification({
            message: "Employee Details added successfully.",
            type: "success"
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
          // setNotification(err.errors);
          setNotification({
            message: `${err.errors}`,
            type: "error"
          });
        } else {
          console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission: ${err}`,
            type: "error"
          });
        }
      }
    },
    [editingRequest, addEmploymentDetails, updateEmploymentDetails, closeModal]
  );

  const openModalForAdd = () => {
    setEditingRequest(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (request) => {
    setEditingRequest(request);
    setIsModalOpen(true);
  };

  const excludeFilterColumns = useMemo(() => ["actions", "category", "job_title", "employee_id"], []);
  

  // Memoized Columns Definition
  const columns = useMemo(
    () => [
      { label: "EMP ID", 
        field: "employee_id", 
        render: (row) => (
        <div
          onClick={() => {openDrawerForDetails(row); setOpenDropdownId(null);}}
          className="text-sm text-blue-500 hover:underline hover:underline-offset-4 cursor-pointer">
          {row.employee_id}
        </div>
      ) },
      { label: "Designation", field: "designation" },
      { label: "Department", field: "department" },
      { label: "Type", field: "employment_type" },
      { label: "Status", field: "status",
        render: (row) => (
          <StatusBadge status={row.status} />
        )
       },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                onClick={(e) => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              
              {openDropdownId === row.id && (
                <div className={`absolute right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] min-w-[160px] ${
                  dropdownPosition === 'top' 
                    ? 'bottom-full mb-1' 
                    : 'top-full mt-1'
                }`} style={{
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <div className="flex flex-col space-y-0">
                    {/* View */}
                    <button
                      onClick={() => {
                        openDrawerForDetails(row);
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
                    >
                      View
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => {
                        openModalForEdit(row);
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ],
    [
      openDropdownId,
      dropdownPosition,
      handleDropdownToggle,
      openDrawerForDetails,
      openModalForEdit,
    ]
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchEmploymentDetails(newPage);
    }
  };
  
  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-2 rounded-md mx-1 ${
            currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const handleDeleteEmploymentDetail = async (id) => {
    try {
      await deleteEmploymentDetails(id);
      // setNotification("Employment Detail deleted successfully");
      setNotification({
        message: "Employment Detail deleted successfully",
        type: "success"
      });
    } catch (error) {
      // setNotification("Failed to delete employment detail");
      setNotification({
        message: "Failed to delete employment detail",
        type: "error"
      });
    }
  };

  // Simple org chart component
  function OrgChart({ employment }) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <p className="text-sm font-medium text-gray-600 dark:text-white mb-3">Organizational Structure</p>
        <div className="flex flex-col items-center">
          <div className="bg-blue-100 px-4 py-2 rounded-lg border border-blue-200 mb-2 w-full text-center">
            <p className="font-medium text-blue-800">{employment.department}</p>
          </div>
          <div className="h-4 w-px bg-gray-300"></div>
          <div className="bg-blue-100 px-4 py-2 rounded-lg border border-blue-200 mb-2 w-4/5 text-center">
            <p className="font-medium text-blue-800">{employment.division}</p>
          </div>
          <div className="h-4 w-px bg-gray-300"></div>
          <div className="bg-purple-100 px-4 py-2 rounded-lg border border-purple-200 w-3/5 text-center">
            <p className="font-medium text-purple-800">{employment.designation}</p>
          </div>
        </div>
      </div>
    );
  }

  // Helper component for displaying information
  function InfoItem({ label, value }) {
    return (
      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
        <p className="text-sm font-medium text-gray-600 mb-1 dark:text-white dark:font-bold">{label}</p>
        <p className="text-gray-800 dark:text-gray-300">{value || 'N/A'}</p>
      </div>
    );
  }

  // Date card component
  function DateCard({ title, date, description }) {
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      } catch (e) {
        return dateStr;
      }
    };

    return (
      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="font-medium text-gray-800 dark:text-white">{title}</h5>
            <p className="text-sm text-gray-500 mt-1 dark:text-gray-200">{description}</p>
          </div>
          <div className="bg-white px-3 py-2 rounded border border-gray-200 text-center min-w-24">
            <p className="text-gray-800 font-medium">{formatDate(date)}</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <Layout title="Employment Management">
      <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
        {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
        )}
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-lg font-medium dark:text-white">Employment List</h2>
        </div>

        <div>
          {/* data table */}
          <ReusableTable
            loading={loading}
            data={employmentDetails}
            columns={columns}
            filterable={true}
            excludeFilterColumns={excludeFilterColumns}
            onFilterChange={(filtering) => setIsFiltering(filtering)}
          />
          {/* data table */}
          
          {/* Pagination */}
          {!isFiltering && employmentDetails?.length > 0 && (
            // <ReactPaginate
            //   breakLabel="..."
            //   nextLabel="Next >"
            //   onPageChange={(event) => handlePageChange(event.selected + 1)}
            //   forcePage={currentPage - 1}
            //   pageRangeDisplayed={5}
            //   disabledClassName='opacity-50 cursor-not-allowed'
            //   disabledLinkClassName='pointer-events-none'
            //   pageCount={pagination.totalPages}
            //   previousLabel="< Prev"
            //   previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800'
            //   nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
            //   renderOnZeroPageCount={null}
            //   containerClassName="flex items-center justify-center mt-4 mb-1 font-poppins"
            //   pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
            //   activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            //   />

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(event) => handlePageChange(event.selected + 1)}
              forcePage={currentPage - 1}
              pageRangeDisplayed={5}
              disabledClassName='opacity-50 cursor-not-allowed'
              disabledLinkClassName='pointer-events-none'
              pageCount={pagination.totalPages}
              previousLabel="< Prev"
              previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
              previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
              nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center mt-4 font-poppins"
              pageClassName="mx-1 border rounded dark:text-white font-poppins"
              pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
              activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
            />
          )}
        </div>

        {/* Modal for Adding or Editing */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          title={editingRequest ? "Edit Employment Details" : "Add New Employment Detail"}>
          <EmploymentDetailForm
            closeModal={closeModal}
            onSubmit={handleEmploymentDetailSubmit}
            initialValues={editingRequest || {}}
            isEditing={Boolean(editingRequest)}
            loading={loading}
          />
        </Modal>

        {/* Drawer for employment Details */}
        <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Employment Details" ChilDiv="" ChildParent="" height_width="max-w-4xl md:max-h-3xl" border_radius="md:rounded-md" showClose = ''>
          {selectedEmployment ? (
          <div>
            <div className={`bg-white dark:bg-gray-900 w-full transform transition-all duration-300 `}>
              {/* Drawer Header */}
              <div className="border-b dark:border-gray-700 border-gray-200 p-4 flex justify-between items-center bg-[#0077FE] text-white">
                <div className='flex items-center gap-4'>
                  <h2 className="text-lg font-medium">Employment Details</h2>
                  <button
                    onClick={() => {openModalForEdit(selectedEmployment);setIsDrawerOpen(false);}}
                    title='Edit'>
                    <Edit className="w-5 h-5 text-white" />
                  </button>
                </div>
                <div className='flex items-center gap-2'>
                  <button onClick={() => setIsDrawerOpen(false)} className="p-1 rounded-full hover:bg-blue-700 transition-colors">
                    <X size={20} />
                  </button>
                </div>
              </div>
      
              {selectedEmployment ? (
                <div className="h-full flex flex-col">
                  {/* Employment Banner */}
                  <div className="bg-blue-50 dark:bg-gray-900 dark:border-gray-700 p-6 border-b border-blue-100">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Briefcase size={20} className="text-[#0077FE]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{selectedEmployment.department || "-"}</h3>
                        <p className="text-sm text-gray-600">{selectedEmployment.designation || "-"}</p>
                      </div>
                    </div>
                    <div className="flex items-center mt-2 text-sm">
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center">
                        <Clock size={14} className="mr-1" />
                        <span>{selectedEmployment.employment_type || "-"}</span>
                      </div>
                      <div className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          selectedEmployment.status === 'active' ? 'bg-blue-500' : 'bg-gray-500'
                        }`}></div>
                        <span>{selectedEmployment.status || "-"}</span>
                      </div>
                    </div>
                  </div>
      
                  {/* Navigation Tabs */}
                  <div className="border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 bg-gray-50">
                    <div className="flex overflow-x-auto py-2 px-4 hide-scrollbar">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => setActiveSection(section.id)}
                          className={`flex items-center px-3 py-2 text-sm rounded-md mr-2 whitespace-nowrap ${
                            activeSection === section.id
                              ? 'bg-blue-100 dark:bg-gray-900 dark:text-white text-blue-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-900'
                          }`}
                        >
                          <span className="mr-2">{section.icon}</span>
                          {section.label}
                        </button>
                      ))}
                    </div>
                  </div>
      
                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {activeSection === 'basic' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 border-b pb-2 dark:border-gray-700 dark:text-white">Basic Employment Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InfoItem label="Employee ID" value={selectedEmployment.employee_id || "-"} />
                          {/* <InfoItem label="Employment ID" value={selectedEmployment.employment_id || "-"} /> */}
                          {/* <InfoItem label="Job Title" value={selectedEmployment.job_title || "-"} /> */}
                          <InfoItem label="Designation" value={selectedEmployment.designation || "-"} />
                          <InfoItem label="Employment Type" value={selectedEmployment.employment_type || "-"} />
                          {/* <InfoItem label="Employment Status" value={selectedEmployment.employment_status || "-"} /> */}
                          <InfoItem label="State Code" value={selectedEmployment.state_code || "-"} />
                        </div>
                      </div>
                    )}
      
                    {activeSection === 'organization' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 border-b pb-2 dark:border-gray-700 dark:text-white">Organizational Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InfoItem label="Department" value={selectedEmployment.department || "-"} />
                          <InfoItem label="Division" value={selectedEmployment.division || "-"} />
                          <InfoItem label="Branch" value={selectedEmployment.branch || "-"} />
                          <InfoItem label="Category" value={selectedEmployment.category || "-"} />
                          <InfoItem label="Status" value={selectedEmployment.status || "-"} />
                          <InfoItem label="PF/UAN" value={selectedEmployment.pf_uan || "-"} />
                          <InfoItem label="ESIC No." value={selectedEmployment.esic_no || "-"} />
                          <InfoItem label="Notice Period Days" value={selectedEmployment.notice_period_days || "-"} />
                          <InfoItem label="Probation Period Days" value={selectedEmployment.probation_period_days || "-"} />
                          {/* <InfoItem label="Exit Type" value={selectedEmployment.exit_type || "-"} /> */}
                        </div>
                      </div>
                    )}
      
                    {activeSection === 'dates' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 border-b dark:border-gray-700 pb-2 dark:text-white">Important Dates</h4>
                        <div className="space-y-4">
                          <DateCard 
                            title="Joining Date" 
                            date={selectedEmployment.joining_date} 
                            description="Date of joining the organization" 
                          />
                        </div>
                      </div>
                    )}
      
                    {activeSection === 'location' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 border-b pb-2 dark:border-gray-700 dark:text-white">Work Location</h4>
                        <div className="bg-gray-50 dark:bg-gray-700 p-5 rounded-lg flex items-start">
                          <MapPin size={20} className="text-gray-500 mr-3 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-gray-800 whitespace-pre-line">{selectedEmployment.branch || "-"}</p>
                          </div>
                        </div>
                      </div>
                    )}
      
                    {activeSection === 'grade' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 border-b pb-2 dark:border-gray-700 dark:text-white">Grade & Salary Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-white">Employee Grade</p>
                                <p className="text-lg font-semibold text-gray-800 mt-1">{selectedEmployment.grade || "-"}</p>
                              </div>
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Award size={20} className="text-blue-600" />
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-white">Salary Grade</p>
                                <p className="text-lg font-semibold text-gray-800 mt-1">{selectedEmployment.salary_grade || "-"}</p>
                              </div>
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Grid size={20} className="text-blue-600" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <p className="text-gray-500">No employment details to display</p>
                </div>
              )}
            </div>
          </div>

          ) : (
            <p className="text-gray-500">No data to display</p>
          )}
        </Drawer>

      </div>
    </Layout>
  );
}
